import { Action, createReducer, on } from '@ngrx/store';
import { UserState, initialState } from './users.state';

import * as UserActions from './user.actions';

const usersReducer = createReducer(
  initialState,
  on(UserActions.loginSuccess, (state, { response }) => {
    const { user, token } = response;
    return { ...state, user, token, loggedIn: true };
  }),
  on(UserActions.loginFailed, (state, { err }) => {
    return { ...state, error: err.error?.description };
  }),
  on(UserActions.registerSuccess, (state, { user }) => {
    return { ...state, registrationSuccess: true, user: user };
  }),
  on(UserActions.registerFailed, (state, { err }) => {
    return {
      ...state,
      registrationSuccess: false,
      error:
        err.error?.message ||
        err.error?.description ||
        err.message ||
        'Registration failed',
    };
  }),
  on(UserActions.registerIdentify, (state) => ({
    ...state,
    error: null,
    registrationToken: '',
  })),
  on(UserActions.registerIdentifySuccess, (state) => ({
    ...state,
    error: null,
    otp: { expiresOn: new Date(new Date().getTime() + 5 * 60 * 1000) },
    registrationToken: '',
  })),
  on(UserActions.registerIdentifyFailed, (state, { err }) => ({
    ...state,
    error:
      err.error?.message ||
      err.error?.description ||
      err.message ||
      'Verification request failed',
  })),
  on(UserActions.registerVerify, (state) => ({
    ...state,
    error: null,
  })),
  on(UserActions.registerVerifySuccess, (state, { registrationToken }) => ({
    ...state,
    error: null,
    registrationToken,
  })),
  on(UserActions.registerVerifyFailed, (state, { err }) => ({
    ...state,
    error:
      err.error?.message ||
      err.error?.description ||
      err.message ||
      'Verification code invalid or expired',
  })),
  on(UserActions.registerComplete, (state) => ({
    ...state,
    error: null,
  })),
  on(UserActions.registerCompleteSuccess, (state) => ({
    ...state,
    error: null,
  })),
  on(UserActions.registerCompleteFailed, (state, { err }) => ({
    ...state,
    error:
      err.error?.message ||
      err.error?.description ||
      err.message ||
      'Registration failed',
  })),
  on(UserActions.userVerificationFailed, (state, { err }) => {
    return {
      ...state,
      error: err,
    };
  }),
  on(UserActions.sendVerificationCode, (state) => {
    return {
      ...state,
      error: null,
    };
  }),
  on(UserActions.sendResetPasswordRequest, (state) => {
    return {
      ...state,
      error: null,
    };
  }),
  on(UserActions.sendVerificationCodeFailed, (state, { err }) => {
    return {
      ...state,
      error: err.error?.description,
    };
  }),
  on(UserActions.sendVerificationCodeSuccess, (state) => {
    return {
      ...state,
      otp: {
        expiresOn: new Date(new Date().getTime() + 5 * 60 * 1000),
      },
    };
  }),
  on(UserActions.verifyUser, (state) => {
    return {
      ...state,
      error: null,
    };
  }),
  on(UserActions.logout, () => {
    return initialState;
  }),
  on(UserActions.resetPasswordRequestSuccess, (state, { resetToken }) => {
    return {
      ...state,
      resetToken: resetToken,
    };
  }),
  on(UserActions.resetPasswordRequestFailed, (state, { err }) => {
    return {
      ...state,
      error: err.error?.description,
    };
  }),
  on(UserActions.resetPasswordFailed, (state) => {
    return {
      ...state,
      resetToken: '',
    };
  }),
  on(UserActions.initiateResetPasswordFlow, (state) => {
    return {
      ...state,
      resetToken: '',
      resetPasswordSuccess: false,
    };
  }),
  on(UserActions.resetPasswordSuccess, (state) => {
    return {
      ...state,
      resetToken: '',
      resetPasswordSuccess: true,
    };
  }),
  on(UserActions.updateUserProfileSuccess, (state, { user }) => {
    return {
      ...state,
      user: user,
    };
  }),
  on(UserActions.clearUserState, () => {
    return initialState;
  }),
  on(UserActions.rehydrateUserState, (state, { userState }) => {
    return { ...state, ...userState };
  }),
  on(UserActions.requestImpersonation, (state, { userId }) => ({
    ...state,
    impersonationTargetUserId: userId,
    impersonationPending: true,
    impersonationRequested: false,
    impersonationError: null,
  })),
  on(UserActions.requestImpersonationSuccess, (state) => ({
    ...state,
    impersonationPending: false,
    impersonationRequested: true,
    impersonationError: null,
  })),
  on(UserActions.requestImpersonationFailed, (state, { err }) => ({
    ...state,
    impersonationPending: false,
    impersonationRequested: false,
    impersonationError: err.error?.message || 'Failed to send OTP',
  })),
  on(UserActions.verifyImpersonation, (state) => ({
    ...state,
    impersonationError: null,
  })),
  on(UserActions.verifyImpersonationSuccess, (state, { token, user }) => ({
    ...state,
    user: user as any,
    token,
    loggedIn: true,
    isImpersonating: true,
    impersonationPending: false,
    impersonationRequested: false,
    impersonationTargetUserId: null,
    impersonationError: null,
  })),
  on(UserActions.verifyImpersonationFailed, (state, { err }) => ({
    ...state,
    impersonationError: err.error?.message || 'Invalid OTP',
  })),
  on(UserActions.endImpersonation, (state) => ({
    ...state,
    impersonationError: null,
  })),
  on(UserActions.endImpersonationSuccess, (state, { user }) => ({
    ...state,
    user: user as any,
    isImpersonating: false,
    impersonationPending: false,
    impersonationRequested: false,
    impersonationTargetUserId: null,
    impersonationError: null,
  })),
  on(UserActions.loadTutors, (state) => ({
    ...state,
    tutorsLoading: true,
  })),
  on(UserActions.loadTutorsSuccess, (state, { response }) => ({
    ...state,
    tutorsLoading: false,
    tutorsList: response,
  })),
  on(UserActions.loadTutorsFailed, (state) => ({
    ...state,
    tutorsLoading: false,
  })),
  on(UserActions.loadTutorProfile, (state) => ({
    ...state,
    tutorProfileLoading: true,
    tutorProfileError: null,
  })),
  on(UserActions.loadTutorProfileSuccess, (state, { response }) => ({
    ...state,
    tutorProfileLoading: false,
    selectedTutorProfile: response,
  })),
  on(UserActions.loadTutorProfileFailed, (state, { err }) => ({
    ...state,
    tutorProfileLoading: false,
    tutorProfileError: err.error?.message || 'Failed to load tutor profile',
  })),
  on(UserActions.updateTutorProfileSuccess, (state, { user }) => ({
    ...state,
    user,
  }))
);

export function reducer(state: UserState | undefined, action: Action) {
  return usersReducer(state, action);
}
